import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { RecentBounce } from '@/lib/types'

interface RecentBouncesTableProps {
  bounces: RecentBounce[]
}

const MAX_RESPONSE_LENGTH = 60

export function RecentBouncesTable({ bounces }: RecentBouncesTableProps): React.JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (bounces.length === 0) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-12 text-center">
        <p className="text-sm text-text-secondary">No recent bounces</p>
      </div>
    )
  }

  function toggleExpand(id: string): void {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <div>
      <h2 className="mb-3 text-sm font-medium text-text-primary">Recent Bounces</h2>
      <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-surface-3 bg-surface-1">
              <tr>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Recipient
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Category
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  SMTP Code
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Response
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Account
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase text-text-secondary">
                  Domain
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-2">
              {bounces.map((bounce) => (
                <BounceRow
                  key={bounce.id}
                  bounce={bounce}
                  isExpanded={expandedId === bounce.id}
                  onToggle={() => toggleExpand(bounce.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

interface BounceRowProps {
  bounce: RecentBounce
  isExpanded: boolean
  onToggle: () => void
}

function BounceRow({ bounce, isExpanded, onToggle }: BounceRowProps): React.JSX.Element {
  const truncatedResponse =
    bounce.smtp_response.length > MAX_RESPONSE_LENGTH
      ? `${bounce.smtp_response.slice(0, MAX_RESPONSE_LENGTH)}...`
      : bounce.smtp_response

  const formattedTime = new Date(bounce.timestamp).toLocaleString()

  return (
    <>
      <tr className="cursor-pointer transition-colors hover:bg-surface-1" onClick={onToggle}>
        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">{formattedTime}</td>
        <td className="whitespace-nowrap px-4 py-3 font-medium text-text-primary">
          {bounce.recipient}
        </td>
        <td className="whitespace-nowrap px-4 py-3">
          <StatusBadge status={bounce.category.toLowerCase()}>{bounce.category}</StatusBadge>
        </td>
        <td className="whitespace-nowrap px-4 py-3 font-mono text-text-secondary">
          {bounce.smtp_code}
        </td>
        <td className="max-w-xs px-4 py-3 text-text-secondary">{truncatedResponse}</td>
        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">{bounce.account_email}</td>
        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">{bounce.domain}</td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-surface-1 px-6 py-4">
            <p className="text-xs font-medium uppercase text-text-secondary">Full SMTP Response</p>
            <p className="mt-1 whitespace-pre-wrap font-mono text-sm text-text-primary">
              {bounce.smtp_response}
            </p>
          </td>
        </tr>
      )}
    </>
  )
}
